import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Search, ShieldCheck, History } from "lucide-react";
import { toast } from "sonner";

const fmtTs = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const COUNT_LABELS = {
  messaggi_inviati: "Messaggi inviati",
  ticket_aperti: "Ticket aperti",
  notifiche_ricevute: "Notifiche",
  documenti_caricati: "Documenti caricati",
  presenze_richieste: "Presenze/richieste",
  corsi_formazione: "Corsi formazione",
};

export const GroupAdminDialog = ({ user, orgs, onClose, onSaved }) => {
  const [enabled, setEnabled] = useState(user.role === "group_admin");
  const [selected, setSelected] = useState(user.group_org_ids || []);
  const [homeOrg, setHomeOrg] = useState(user.org_id || "");
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/superadmin/users/${user.id}/data-summary`).then(({ data }) => setSummary(data)).catch(() => {});
    api.get(`/superadmin/users/${user.id}/audit`).then(({ data }) => setAudit(data)).catch(() => {});
  }, [user.id]);

  const filteredOrgs = useMemo(() => {
    if (!search) return orgs;
    const q = search.toLowerCase();
    return orgs.filter((o) => (o.name || "").toLowerCase().includes(q) || o.id.toLowerCase().includes(q));
  }, [orgs, search]);

  const toggleOrg = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const save = async () => {
    if (enabled && selected.length === 0) {
      toast.error("Seleziona almeno un'organizzazione");
      return;
    }
    if (!enabled && !homeOrg) {
      toast.error("Indica l'organizzazione principale per il ripristino");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/superadmin/users/${user.id}/group-admin`, {
        enabled,
        org_ids: enabled ? selected : [],
        home_org_id: homeOrg || null,
      });
      toast.success("Ruolo aggiornato. Backup automatico salvato.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="group-admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-accent" /> Ruolo e organizzazioni
          </DialogTitle>
          <DialogDescription>
            {user.full_name || user.email} — <span className="font-mono text-xs">{user.email}</span>
          </DialogDescription>
        </DialogHeader>

        {summary && (
          <div className="grid grid-cols-3 gap-2" data-testid="user-data-summary">
            {Object.entries(summary.counts).map(([k, v]) => (
              <div key={k} className="rounded-md border border-border bg-subtle px-2.5 py-2">
                <div className="text-lg font-semibold text-text-primary tabular-nums" data-testid={`summary-${k}`}>{v}</div>
                <div className="text-[10px] text-text-muted leading-tight">{COUNT_LABELS[k] || k}</div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-text-muted -mt-1">
          Riepilogo dati dell&apos;utente: confronta i numeri prima e dopo la modifica — devono coincidere.
        </p>

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <div>
            <div className="text-sm font-medium text-text-primary">Amministratore di gruppo</div>
            <div className="text-xs text-text-muted">Accesso admin SOLO alle organizzazioni scelte qui sotto</div>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="group-admin-toggle" />
        </div>

        {enabled && (
          <div className="space-y-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Cerca organizzazione…" className="pl-8 h-9"
                     data-testid="group-admin-org-search" />
            </div>
            <div className="max-h-44 overflow-y-auto rounded-md border border-border divide-y divide-border">
              {filteredOrgs.map((o) => (
                <label key={o.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-subtle">
                  <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => toggleOrg(o.id)}
                            data-testid={`group-admin-org-${o.id}`} />
                  <span className="text-text-primary">{o.name}</span>
                  <span className="text-[10px] text-text-muted font-mono ml-auto">{o.id}</span>
                </label>
              ))}
              {filteredOrgs.length === 0 && (
                <div className="px-3 py-4 text-xs text-text-muted text-center">Nessuna organizzazione trovata</div>
              )}
            </div>
            <div className="text-xs text-text-muted">
              {selected.length} organizzazion{selected.length === 1 ? "e" : "i"} selezionat{selected.length === 1 ? "a" : "e"}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <div className="text-sm font-medium text-text-primary">Organizzazione principale</div>
          <Select value={homeOrg} onValueChange={setHomeOrg}>
            <SelectTrigger className="h-9" data-testid="group-admin-home-org"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-text-muted">
            Org. di origine dell&apos;utente (dove vivono i suoi dati storici). Usala per <strong>ripristinare</strong> un
            utente promosso a super admin che aveva perso la sua organizzazione: nessun dato viene toccato.
          </p>
        </div>

        {audit.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-sm font-medium text-text-primary flex items-center gap-1.5">
              <History size={14} /> Registro attività
            </div>
            <div className="max-h-36 overflow-y-auto rounded-md border border-border divide-y divide-border" data-testid="group-admin-audit">
              {audit.map((a) => (
                <div key={a.id} className="px-3 py-1.5 text-xs">
                  <span className="text-text-muted tabular-nums">{fmtTs(a.ts)}</span>{" "}
                  <span className="text-text-secondary">· {a.actor_email} ·</span>{" "}
                  <span className="text-text-primary">{a.detail}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose} className="h-9">Annulla</Button>
          <Button onClick={save} disabled={saving} className="h-9" data-testid="group-admin-save">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
